/**
 * Decor.js — the single tree/bush/rock factory.
 *
 * Kills the 3 separate implementations that lived in:
 *   - src/games/snake/SnakeObstacles.js (_drawTree / _drawBush / _drawRock)
 *   - src/games/pipeline/PipelineBoard.js (_drawTree / _drawBush / _drawRock)
 *   - src/games/frogger/RowManager.js (_drawSafeDecor)
 *
 * Public API:
 *   drawTree(g, x, y, w, h, opts?)
 *   drawBush(g, x, y, w, h, opts?)
 *   drawRock(g, x, y, w, h, opts?)
 *
 * `opts.seed`  → deterministic randomness for berries / texture detail
 * `opts.shadow` → draw a ground shadow ellipse (default true)
 */

const DEFAULT_OPTS = { seed: 0, shadow: true };

export function drawTree(g, x, y, w, h, opts = {}) {
    const { shadow } = { ...DEFAULT_OPTS, ...opts };
    const cx = x + w / 2;

    if (shadow) {
        g.ellipse(cx, y + h * 0.72, w * 0.22, h * 0.06);
        g.fill({ color: 0x0a3622, alpha: 0.35 });
    }

    const trunkW = w * 0.16;
    const trunkH = h * 0.26;
    g.roundRect(cx - trunkW / 2, y + h * 0.55, trunkW, trunkH, 2);
    g.fill(0x78350f);
    g.roundRect(cx - trunkW / 2, y + h * 0.55, trunkW * 0.4, trunkH, 2);
    g.fill({ color: 0x92400e, alpha: 0.5 });

    g.circle(cx, y + h * 0.35, w * 0.22);
    g.fill(0x15803d);
    g.circle(cx - w * 0.14, y + h * 0.44, w * 0.17);
    g.fill(0x16a34a);
    g.circle(cx + w * 0.14, y + h * 0.44, w * 0.17);
    g.fill(0x14532d);
    g.circle(cx, y + h * 0.39, w * 0.16);
    g.fill(0x22c55e);

    g.circle(cx - w * 0.06, y + h * 0.32, w * 0.08);
    g.fill({ color: 0x4ade80, alpha: 0.45 });
}

export function drawBush(g, x, y, w, h, opts = {}) {
    const { seed, shadow } = { ...DEFAULT_OPTS, ...opts };

    if (shadow) {
        g.ellipse(x + w / 2, y + h * 0.68, w * 0.18, h * 0.05);
        g.fill({ color: 0x0a3622, alpha: 0.3 });
    }

    g.circle(x + w * 0.34, y + h * 0.6, w * 0.16);
    g.fill(0x14532d);
    g.circle(x + w * 0.5, y + h * 0.48, w * 0.2);
    g.fill(0x16a34a);
    g.circle(x + w * 0.66, y + h * 0.6, w * 0.16);
    g.fill(0x15803d);
    g.circle(x + w * 0.5, y + h * 0.52, w * 0.14);
    g.fill(0x22c55e);

    g.circle(x + w * 0.42, y + h * 0.45, w * 0.06);
    g.fill({ color: 0x4ade80, alpha: 0.4 });

    const safeSeed = seed || Math.round(x * 7 + y * 13);
    const berryColors = [0xef4444, 0xf97316, 0xfbbf24];
    for (let i = 0; i < 3; i++) {
        const bx = x + w * (0.35 + ((safeSeed + i * 37) % 30) / 100);
        const by = y + h * (0.45 + ((safeSeed + i * 53) % 20) / 100);
        g.circle(bx, by, w * 0.03);
        g.fill(berryColors[i % berryColors.length]);
    }
}

export function drawRock(g, x, y, w, h) {
    g.ellipse(x + w * 0.52, y + h * 0.52, w * 0.28, h * 0.2);
    g.fill(0x475569);
    g.ellipse(x + w * 0.48, y + h * 0.48, w * 0.26, h * 0.18);
    g.fill(0x64748b);

    g.ellipse(x + w * 0.44, y + h * 0.44, w * 0.12, h * 0.08);
    g.fill({ color: 0x94a3b8, alpha: 0.5 });

    g.moveTo(x + w * 0.55, y + h * 0.38);
    g.lineTo(x + w * 0.62, y + h * 0.58);
    g.stroke({ color: 0x334155, width: 1, alpha: 0.3 });
}
